import React from "react"
import { observer } from "mobx-react-lite"
import { FieldProps } from "formik"
import without from "lodash/without"
import debounce from "lodash/debounce"

import Dropdown from "@components/prototypes/FilterSidebar/components/Dropdown/Dropdown"
import NotFound from "@components/ui/NotFound/NotFound"
import Loader from "@components/ui/Loader/BarLoader"
import Templates from "@components/ui/Templates"
import { useStore } from "@store/index"
import CheckboxWithLabel from "@components/ui/Checkbox/CheckboxWithLabel"
import ListItem from "@components/ui/ListItem/ListItem"
import DocumentIcon from "@components/ui/Icon/DocumentIcon"
import { IconName } from "@components/ui/Icon/IconName"
import List from "@components/ui/List/List"
import InfiniteCollection from "@components/modals/EditModal/components/BaseContainer/InfiniteCollection"
import { SearchContext } from "@components/prototypes/SearchContext"
import { KibanaConnector } from "@framework/types/content-manager"
import { DataSourceNodeName } from "@framework/types/upload"
import { getDataSourceNodeContextProps } from "@framework/constants/upload"

import styles from "./DataConnectorSelect.module.sass"

const DEBOUNCE_TIMEOUT = 500

export interface DataConnectorSelectProps extends FieldProps<string[]> {
  label?: string
  valueKey?: keyof KibanaConnector
}

const DataConnectorSelect: React.FC<DataConnectorSelectProps> = observer(
  ({ label, field, form, valueKey = "id" }) => {
    const context = React.useContext(SearchContext)

    const [open, setOpen] = React.useState(false)

    const { value = [], name } = field
    const { setFieldValue } = form

    const { allDataConnectorsStore } = useStore()

    const handleClick = (newValue: string) => {
      if (!value.includes(newValue)) {
        setFieldValue(name, [...value, newValue])
      } else {
        setFieldValue(name, without(value, newValue))
      }
    }

    const debouncedSearch = React.useCallback(
      debounce(allDataConnectorsStore.search, DEBOUNCE_TIMEOUT),
      []
    )

    React.useEffect(() => {
      debouncedSearch(context?.query ?? "")
    }, [context?.query])

    const renderRow = (item: KibanaConnector) => {
      const active = value?.includes(item[valueKey] as string)

      const desc = getDataSourceNodeContextProps(
        item.type as DataSourceNodeName
      )

      return (
        <ListItem
          active={active}
          onClick={() => handleClick(item[valueKey] as string)}
        >
          <List direction="row" gutter="8" align="center">
            <DocumentIcon
              icon={(desc.icon as IconName) ?? "global"}
              size="medium"
            />
            {item.name}
          </List>
        </ListItem>
      )
    }

    const isChecked = !!value?.length

    const handleUncheck = (e: React.MouseEvent) => {
      if (isChecked) setFieldValue(name, undefined)
      else setOpen(true)
      e.stopPropagation()
    }

    return (
      <Dropdown
        title={label}
        opened={open}
        onClick={() => setOpen((value) => !value)}
        before={
          <CheckboxWithLabel
            checked={!!value?.length}
            onClick={handleUncheck}
          />
        }
      >
        <Templates.RollScript className={styles.root}>
          <div className={styles.container}>
            {allDataConnectorsStore.state.total === 0 ? (
              allDataConnectorsStore.state.isLoading ? (
                <Loader size="large" fluid />
              ) : (
                <NotFound>No Connector found</NotFound>
              )
            ) : (
              <InfiniteCollection<KibanaConnector>
                total={allDataConnectorsStore.state.total}
                getItem={allDataConnectorsStore.getByIndex}
                renderItem={renderRow}
                minRowHeight={42}
              />
            )}
          </div>
        </Templates.RollScript>
      </Dropdown>
    )
  }
)

export default DataConnectorSelect
